import React, {FC} from 'react';
import styled from 'styled-components';

import {NewPaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ChartProps} from '../../commons/types';
import {getHeatmapColors} from '../../helpers/colors';
import {HeatMapProps} from '../heatmap/heatmap';

/*
 * Constants.
 */

const LOADING_COLOR = NewPaletteColorsEnum.GREY;
const DEFAULT_COLOR = NewPaletteColorsEnum.ORANGE;

const DEFAULT_HEIGHT = 144;
const DEFAULT_WIDTH = 8;

/*
 * Props.
 */

export interface HeatmapLegendProps<T>
  extends Pick<ChartProps<T>, 'data' | 'color' | 'isLoading'>,
    Pick<HeatMapProps<T>, 'colorAccessor'> {
  width?: number;
  height?: number;
}

/*
 * Helpers.
 */
// Will return "#FFF 0%, #111 25% ..., #555 100%" based on the array of colors passed.
const getLegendGradient = (colors: Array<string>) => {
  const nbSections = colors.length - 1;
  return colors.map((color, i) => `${color} ${(i * 100) / nbSections}%${i < nbSections ? ',' : ''}`);
};

/*
 * Style.
 */

const StyledLegend = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLegendLabel = styled.div`
  font-size: ${fontSizes.tiny};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledLegendScale = styled.div<{$binColors: Array<string>; $width: number; $height: number}>`
  width: ${(p) => p.$width}px;
  height: ${(p) => p.$height}px;
  margin: 0 4px;
  background: linear-gradient(90deg, ${(p) => getLegendGradient(p.$binColors)});
`;

/*
 * Component.
 */

export const HeatmapLegend = <T,>({
  color = DEFAULT_COLOR,
  width = DEFAULT_HEIGHT,
  height = DEFAULT_WIDTH,
  data,
  colorAccessor,
  isLoading,
}: HeatmapLegendProps<T>): ReturnType<FC> => {
  const theme = useUserTheme();
  const min = Math.min(...data.map(colorAccessor));
  const max = Math.max(...data.map(colorAccessor));
  const mainColor = isLoading ? LOADING_COLOR : color;
  const {binColors} = getHeatmapColors(theme, mainColor, isLoading);

  return (
    <StyledLegend>
      <StyledLegendLabel>{!isLoading && min.toLocaleString()}</StyledLegendLabel>
      <StyledLegendScale $binColors={binColors} $width={width} $height={height} />
      <StyledLegendLabel>{!isLoading && max.toLocaleString()}</StyledLegendLabel>
    </StyledLegend>
  );
};
